import React, { useEffect } from "react";
import FontAwesome from "react-fontawesome";
import { PlussCore } from "../config/core";
import { get1400 } from "../helper";
import styles from "./ImageViewer.module.css";

// Sourced directly from core (not the ../components barrel) to avoid a circular
// import, since the barrel re-exports this component.
const { Popup } = PlussCore.Components;

/**
 * Full-size gallery image viewer ("lightbox") with previous/next navigation and
 * keyboard control. The parent owns which gallery (`images`) and which position
 * (`index`) is open; the viewer reports movement back via `onChangeIndex`.
 *
 * @param {object[]} images - The image objects for the gallery being viewed.
 * @param {number} index - Index of the currently displayed image within `images`.
 * @param {string} [title] - Popup heading, e.g. the gallery/folder name.
 * @param {(nextIndex: number) => void} onChangeIndex - Move to another image.
 * @param {() => void} onClose - Dismiss the viewer.
 */
export const ImageViewer = ({
	images,
	index,
	title = "View Image",
	onChangeIndex,
	onClose,
}) => {
	const current = images && images[index];
	const hasPrev = index > 0;
	const hasNext = !!images && index < images.length - 1;

	// Arrow keys navigate within bounds; Escape dismisses. Re-bound when the
	// position changes so the bounds checks stay accurate, and torn down on
	// unmount so no stale listener survives the close.
	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				onClose();
				return;
			}
			if (event.key === "ArrowLeft" && index > 0) {
				event.preventDefault();
				onChangeIndex(index - 1);
			} else if (
				event.key === "ArrowRight" &&
				images &&
				index < images.length - 1
			) {
				event.preventDefault();
				onChangeIndex(index + 1);
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [index, images, onChangeIndex, onClose]);

	if (!current) return null;

	return (
		<Popup
			title={title}
			minWidth="80vw"
			buttons={[
				{
					type: "primary",
					onClick: onClose,
					isActive: true,
					text: "Close",
				},
			]}
			hasPadding
		>
			<div className={styles.imageViewer}>
				<div className={styles.imageStage}>
					{hasPrev && (
						<button
							type="button"
							className={`${styles.navButton} ${styles.navPrev}`}
							onClick={() => onChangeIndex(index - 1)}
							aria-label="Previous image"
							title="Previous image"
						>
							<FontAwesome name="chevron-left" />
						</button>
					)}
					<img
						className={styles.imageViewerImage}
						src={get1400(current.uri)}
						alt={current.caption || "Full-size gallery image"}
					/>
					{hasNext && (
						<button
							type="button"
							className={`${styles.navButton} ${styles.navNext}`}
							onClick={() => onChangeIndex(index + 1)}
							aria-label="Next image"
							title="Next image"
						>
							<FontAwesome name="chevron-right" />
						</button>
					)}
				</div>
				{current.caption && (
					<p className={styles.imageViewerCaption}>{current.caption}</p>
				)}
			</div>
		</Popup>
	);
};

export default ImageViewer;
